import React, { useContext } from "react";
import { StageContext } from "../../contexes/OrderIdContext";
import { segmentCode } from "./trackstarsText";
import { luscaSearchPath } from "./investigationUtils";
import "./CodeProse.scss";

/**
 * An order ID or AUID (UUID) as a code chip that opens the transaction in Lusca Search for the
 * page's stage (new tab). Without a stage in context it stays a plain chip.
 */
export const TransactionIdChip: React.FC<{
  id: string;
  stage?: string;
  /** Show the Lusca Search favicon: the author pasted an explicit Lusca Search link. */
  withIcon?: boolean;
}> = ({ id, stage, withIcon }) => {
  const contextStage = useContext(StageContext);
  const s = stage || contextStage;
  const chip = <code className="code-prose code-prose--id">{id}</code>;
  if (!s) return chip;
  return (
    <a
      className={
        withIcon ? "code-prose-link code-prose-link--lusca" : "code-prose-link"
      }
      href={luscaSearchPath(s, id)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${id} in Lusca Search`}
      title={withIcon ? `Lusca Search (${s})` : undefined}
    >
      {withIcon && (
        <img
          className="code-prose-link__icon"
          src="/favicon.png"
          alt=""
          aria-hidden="true"
        />
      )}
      {chip}
    </a>
  );
};

/**
 * Agent prose with its identifiers rendered as inline code, so a sentence like "drops
 * digital_invoice_pipeline_source_unique_id on the reversal branch of DcdCbeRecordGenerator.build()"
 * can be scanned: the words read as words, the symbols stand out as symbols. Order IDs and
 * AUIDs (UUIDs) are chips that link to Lusca Search.
 */
export const CodeProse: React.FC<{ text: string }> = ({ text }) => (
  <>
    {segmentCode(text).map((seg, i) => {
      if (seg.kind)
        // eslint-disable-next-line react/no-array-index-key
        return <TransactionIdChip key={i} id={seg.text} />;
      if (seg.code)
        return (
          <code
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={seg.kind ? "code-prose code-prose--id" : "code-prose"}
          >
            {seg.text}
          </code>
        );
      // eslint-disable-next-line react/no-array-index-key
      return <React.Fragment key={i}>{seg.text}</React.Fragment>;
    })}
  </>
);

export default CodeProse;
